<script setup>
import { currentDayRange } from '../dateTime/historyDateTime.js'
import { useI18n } from '../i18n.js'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, FunnelIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline'
import Pts2ControllerSelector from '../components/pts2/Pts2ControllerSelector.vue'
import Pts2EntityBadge from '../components/pts2/Pts2EntityBadge.vue'
import Pts2HistoryExport from '../components/pts2/Pts2HistoryExport.vue'
import Pts2FilterChip from '../components/pts2/Pts2FilterChip.vue'
import Pts2TankMeasurementDrawer from '../components/pts2/Pts2TankMeasurementDrawer.vue'
import Pts2TankMeasurementFilters from '../components/pts2/Pts2TankMeasurementFilters.vue'
import { normalizePts2Context } from '../pts2/context.js'
import { getPts2TankMeasurementDetail, getPts2TankMeasurementOptions, getPts2TankMeasurements, paginationItems, tankMeasurementRangeIssue } from '../pts2/tankMeasurements.js'
import { choosePts2Controller, loadPts2HistoryContext, session } from '../session.js'
import { createFacetLifecycle, emptyFacetOptions, facetItemsWithSelection, retainFacetSelection } from '../pts2/filterOptions.js'
import { useHistorySession } from '../pts2/historySession.js'
import { historyScope } from '../pts2/historyState.js'
import '../pts2/controllers.css'

const { locale, t } = useI18n()

const context = computed(() => normalizePts2Context(session.pts2Context ?? {}))
const page = ref(emptyPage())
const pageNumber = ref(1)
const pageSize = ref(25)
const filtersOpen = ref(false)
const loading = ref(true)
const refreshing = ref(false)
const switching = ref(false)
const error = ref('')
const filterError = ref('')
const updatedAt = ref(null)
const options = reactive({ TANK: emptyOptions(), FUEL: emptyOptions() })
const applied = reactive(defaultFilters())
const draft = reactive(draftFrom(applied))
const selectedId = ref(null)
const detail = ref(null)
const detailLoading = ref(false)
const detailError = ref('')
let listRequest
let detailRequest
let generation = 0, ready = false, disposed = false, optionTimer
const denied = ref(false)
const scope = () => historyScope(session)

const cachedStale = ref(false)
const history = useHistorySession('measurements', { applied, draft, pageNumber, pageSize, options, denied })
const unapplied = history.unapplied

const resultFrom = computed(() => page.value.totalItems ? (page.value.page - 1) * page.value.limit + 1 : 0)
const resultTo = computed(() => page.value.totalItems ? resultFrom.value + page.value.items.length - 1 : 0)
const pageButtons = computed(() => paginationItems(page.value.page, page.value.totalPages))
const activeFilterGroups = computed(() => Number(applied.tankNumbers.length > 0) + Number(applied.fuelGradeIds.length > 0)
  + Number(applied.statusMode !== 'ALL') + Number(applied.alarmMode !== 'ALL') + Number(applied.completenessMode !== 'ALL'))
const hasConditions = computed(() => activeFilterGroups.value > 0)
const activeChips = computed(() => {
  const result = []
  for (const number of applied.tankNumbers) result.push({ key: `tank:${number}`, label: t('pts2UiHistoryTankNumber', { number }) })
  for (const id of applied.fuelGradeIds) {
    const fuel = facetItemsWithSelection(options.FUEL).find((item) => item.number === id)
    result.push({ key: `fuel:${id}`, entityId: id, appearance: 'entity', label: fuel?.name || t('pts2UiHistoryFuelNumber', { number: id }) })
  }
  if (applied.statusMode === 'OK') result.push({ key: 'status', label: t('pts2UiHistoryStateNormal') })
  if (applied.statusMode === 'ERROR') result.push({ key: 'status', label: t('pts2UiHistoryStateError') })
  if (applied.alarmMode === 'PRESENT') result.push({ key: 'alarm', label: t('pts2UiHistoryAlarmsPresent') })
  if (applied.alarmMode === 'ABSENT') result.push({ key: 'alarm', label: t('pts2UiHistoryNoAlarms') })
  if (applied.completenessMode === 'INCOMPLETE') result.push({ key: 'complete', label: t('pts2UiHistoryIncomplete') })
  return result
})
const shortcuts = computed(() => [
  { key: 'ALL', label: t('pts2UiHistoryAll'), count: page.value.summary.all, icon: null, active: applied.statusMode === 'ALL' && applied.alarmMode === 'ALL' && applied.completenessMode === 'ALL' },
  { key: 'ALARMS', label: t('pts2UiHistoryWithAlarms'), count: page.value.summary.withAlarms, icon: ExclamationTriangleIcon, active: applied.alarmMode === 'PRESENT' },
  { key: 'ERRORS', label: t('pts2UiHistoryErrors'), count: page.value.summary.errors, icon: XCircleIcon, active: applied.statusMode === 'ERROR' },
  { key: 'INCOMPLETE', label: t('pts2UiHistoryIncomplete'), count: page.value.summary.incomplete, icon: InformationCircleIcon, active: applied.completenessMode === 'INCOMPLETE' },
])

const alarmLabels = computed(() => ({
  CriticalHighProduct: t('pts2UiHistoryCriticalHigh'), HighProduct: t('pts2UiHistoryHigh'), LowProduct: t('pts2UiHistoryLow'),
  CriticalLowProduct: t('pts2UiHistoryCriticalLow'), HighWater: t('pts2UiHistoryHighWater'), TankLeakage: t('pts2UiHistoryLeakage'),
}))
const stateLabels = computed(() => ({ NORMAL: t('pts2UiHistoryNormal'), ALARM: t('pts2UiHistoryAlarm'), ERROR: t('pts2UiHistoryError'), INCOMPLETE: t('pts2UiHistoryIncomplete') }))
const stateIcons = { NORMAL: CheckCircleIcon, ALARM: ExclamationTriangleIcon, ERROR: XCircleIcon, INCOMPLETE: InformationCircleIcon }

function emptyPage() { return { items: [], page: 1, limit: 25, totalItems: 0, totalPages: 0, summary: { all: 0, withAlarms: 0, errors: 0, incomplete: 0 } } }
function emptyOptions() { return emptyFacetOptions() }
function initialRange() {
  return currentDayRange()
}
function defaultFilters() { return { ...initialRange(), tankNumbers: [], fuelGradeIds: [], statusMode: 'ALL', alarmMode: 'ALL', completenessMode: 'ALL' } }
function draftFrom(value) { return { ...value, tankNumbers: [...value.tankNumbers], fuelGradeIds: [...value.fuelGradeIds] } }
function criteria(value = applied) { return { stationId: context.value.stationId, controllerId: context.value.activeControllerId, ...value,
  tankNumbers: [...value.tankNumbers], fuelGradeIds: [...value.fuelGradeIds] } }

const optionCriteria = () => criteria()
let observedZone = null, optionZoneGeneration = 0
const optionScope = () => JSON.stringify([scope(), optionZoneGeneration])
function observeOptionsZone(items) {
  const zone = items.find((item) => item.stationTimeZone)?.stationTimeZone
  if (!zone) return
  if (observedZone && observedZone !== zone) {
    optionZoneGeneration++; facets.sync()
    if (filtersOpen.value && ready && !denied.value) facets.ensure()
  }
  observedZone = zone
}
const facets = createFacetLifecycle({ states: options, getCriteria: optionCriteria, getScope: optionScope,
  isValid: (value) => ready && !denied.value && !switching.value && Number(value.stationId) === Number(session.platformContext?.activeStationId) && !tankMeasurementRangeIssue(value.from, value.to), load: getPts2TankMeasurementOptions,
  onDenied: loseAccess,
})

function facetsValid(value) { return !tankMeasurementRangeIssue(value.from, value.to) }
async function initialize() {
  ready = false; loading.value = true; error.value = ''
  try {
    const value = await loadPts2HistoryContext({ isCurrent: () => !disposed })
    if (!value || disposed) return
    if (!context.value.activeControllerId) { history.deny(); loading.value = false; return }
    const retained = history.restore()
    ready = true
    if (filtersOpen.value) facets.ensure()
    if (retained?.page) {
      page.value = retained.page; updatedAt.value = new Date(retained.fetchedAt).toISOString()
      cachedStale.value = !retained.fresh; loading.value = false
      if (!retained.fresh) await refresh()
    } else await refresh({ initial: true })
  } catch (cause) {
    if (disposed) return
    if ([401, 403, 404].includes(cause?.status)) loseAccess(cause)
    else { history.deny(); error.value = message(cause); loading.value = false }
  }
}

async function refresh({ resetPage = false, initial = false } = {}) {
  if (denied.value || !context.value.stationId || !context.value.activeControllerId) { page.value = emptyPage(); loading.value = false; return }
  if (!facetsValid(applied)) return
  if (resetPage) pageNumber.value = 1
  if (!history.matchesQuery()) { page.value = emptyPage(); cachedStale.value = false }
  const descriptor = history.query()
  listRequest?.abort(); listRequest = new AbortController()
  const request = listRequest, current = ++generation, requestScope = scope()
  if (initial || !page.value.items.length) loading.value = true
  refreshing.value = true; error.value = ''
  try {
    const currentCriteria = criteria()
    const result = await getPts2TankMeasurements(currentCriteria, { page: pageNumber.value, limit: pageSize.value, signal: request.signal })
    if (request.signal.aborted || current !== generation || requestScope !== scope() || disposed) return
    observeOptionsZone(result.items)
    page.value = result
    if (result.totalPages && result.page > result.totalPages) { pageNumber.value = result.totalPages; return refresh() }
    if (!result.totalItems && result.page !== 1) { pageNumber.value = 1; return refresh() }
    updatedAt.value = new Date().toISOString()
    pageNumber.value = result.page
    history.saveResult({ ...descriptor, pageNumber: result.page }, result); cachedStale.value = false
  } catch (cause) {
    if (cause?.name !== 'AbortError' && current === generation && requestScope === scope() && !disposed) {
      if ([401, 403, 404].includes(cause?.status)) { loseAccess(cause); return }
      error.value = message(cause)
    }
  } finally {
    if (current === generation) { loading.value = false; refreshing.value = false }
  }
}
function loadOption(facet, cursor, force = false) {
  if (!ready || denied.value || switching.value) return
  return facets.load(facet, cursor === undefined ? options[facet].requestedCursor : cursor, { force })
}
function reloadOptions() { if (ready && !denied.value) facets.refresh() }
async function selectController(id) {
  clearTimeout(optionTimer); clearOptions(); switching.value = true; error.value = ''; closeDrawer()
  try {
    const value = await choosePts2Controller(id)
    if (!value || disposed) return
    clearOptions(); Object.assign(applied, defaultFilters()); Object.assign(draft, draftFrom(applied))
    await refresh({ resetPage: true, initial: true })
  } catch (cause) { error.value = message(cause) }
  finally { switching.value = false; if (filtersOpen.value && !denied.value) facets.ensure() }
}
async function applyFilters() {
  const issue = tankMeasurementRangeIssue(draft.from, draft.to)
  if (issue === 'REQUIRED') { filterError.value = t('pts2UiHistoryRangeRequired'); return }
  if (issue === 'ORDER') { filterError.value = t('pts2UiHistoryRangeOrder'); return }
  if (issue === 'TOO_LONG') { filterError.value = t('pts2UiHistoryRangeTooLong'); return }
  Object.assign(applied, draftFrom(draft)); filtersOpen.value = false; filterError.value = ''; closeDrawer()
  await Promise.all([refresh({ resetPage: true }), facets.ensure()])
}
async function clearFilters() {
  Object.assign(draft, draftFrom(defaultFilters())); filterError.value = ''; filtersOpen.value = true
}
async function selectShortcut(key) {
  Object.assign(draft, { statusMode: key === 'ERRORS' ? 'ERROR' : 'ALL', alarmMode: key === 'ALARMS' ? 'PRESENT' : 'ALL',
    completenessMode: key === 'INCOMPLETE' ? 'INCOMPLETE' : 'ALL' })
  filterError.value = ''; filtersOpen.value = true
}
async function removeChip(key) {
  if (key.startsWith('tank:')) draft.tankNumbers = draft.tankNumbers.filter((id) => id !== Number(key.slice(5)))
  else if (key.startsWith('fuel:')) draft.fuelGradeIds = draft.fuelGradeIds.filter((id) => id !== Number(key.slice(5)))
  else if (key === 'status') draft.statusMode = 'ALL'
  else if (key === 'alarm') draft.alarmMode = 'ALL'
  else if (key === 'complete') draft.completenessMode = 'ALL'
  filterError.value = ''; filtersOpen.value = true
}
async function goToPage(value) { pageNumber.value = value; closeDrawer(); await refresh() }
async function changePageSize(value) { pageSize.value = Number(value); await refresh({ resetPage: true }) }
async function openMeasurement(item) {
  selectedId.value = item.id; detail.value = null; detailError.value = ''; detailLoading.value = true
  detailRequest?.abort(); const request = new AbortController(); detailRequest = request
  const requestScope = scope()
  try {
    const value = await getPts2TankMeasurementDetail(context.value.stationId, context.value.activeControllerId, item.id, { signal: request.signal })
    if (!request.signal.aborted && detailRequest === request && selectedId.value === item.id && requestScope === scope() && !disposed) detail.value = value
  } catch (cause) {
    if (cause?.name === 'AbortError' || detailRequest !== request || selectedId.value !== item.id || requestScope !== scope() || disposed) return
    if ([401, 403, 404].includes(cause?.status)) loseAccess(cause)
    else detailError.value = message(cause)
  }
  finally { if (detailRequest === request && !request.signal.aborted) detailLoading.value = false }
}
function closeDrawer() { detailRequest?.abort(); selectedId.value = null; detail.value = null; detailError.value = ''; detailLoading.value = false }
function clearOptions() { clearTimeout(optionTimer); facets.clear() }
function loseAccess(cause) {
  history.deny(); cachedStale.value = false
  denied.value = true; generation++; listRequest?.abort(); clearOptions(); closeDrawer(); page.value = emptyPage()
  Object.assign(applied, defaultFilters()); Object.assign(draft, draftFrom(applied))
  error.value = message(cause); loading.value = false; refreshing.value = false
}

function message(value) { return [401, 403, 404].includes(value?.status) ? t('pts2UiHistoryAccessDenied') : t('pts2UiHistoryLoadMeasurementsError') }
function date(value, timeZone) {
  if (!value) return '—'; const format = { dateStyle: 'short', timeStyle: 'medium' }; if (timeZone) format.timeZone = timeZone
  return new Intl.DateTimeFormat(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', format).format(new Date(value))
}
function number(value, digits = 1) {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = Number(value); return Number.isFinite(parsed) ? parsed.toLocaleString(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : value
}
function alarmText(item) {
  if (item.alarmDataState !== 'KNOWN') return t('pts2UiHistoryIncomplete')
  return item.alarms.length ? item.alarms.map((value) => alarmLabels[value] || value).join(', ') : t('pts2UiHistoryNone')
}

watch(scope, () => {
  history.resetOwner(); cachedStale.value = false; filtersOpen.value = false
  observedZone = null
  generation++; listRequest?.abort(); clearTimeout(optionTimer); clearOptions(); closeDrawer(); page.value = emptyPage()
  updatedAt.value = null; loading.value = false; refreshing.value = false; denied.value = false
  Object.assign(applied, defaultFilters()); Object.assign(draft, draftFrom(applied))
  if (ready && !switching.value && session.user && context.value.activeControllerId) { if (filtersOpen.value) facets.ensure(); refresh({ resetPage: true, initial: true }) }
}, { flush: 'sync' })
watch(optionCriteria, () => {
  clearTimeout(optionTimer)
  retainFacetSelection(options.TANK, draft.tankNumbers); retainFacetSelection(options.FUEL, draft.fuelGradeIds)
  facets.sync()
  if (ready && filtersOpen.value && !switching.value && !denied.value) optionTimer = setTimeout(() => facets.ensure(), 150)
}, { deep: true })
watch(draft, () => {
  filterError.value = ''
  retainFacetSelection(options.TANK, draft.tankNumbers); retainFacetSelection(options.FUEL, draft.fuelGradeIds)
}, { deep: true })
watch(filtersOpen, (open) => { if (open && ready && !switching.value && !denied.value) facets.ensure() })
onMounted(initialize)
onBeforeUnmount(() => { history.capture(); disposed = true; ready = false; generation++; clearTimeout(optionTimer); facets.dispose(); listRequest?.abort(); detailRequest?.abort() })
</script>

<template>
  <section class="pc-page pts2-transactions-page pts2-measurements-page">
    <header class="pts2-transactions-heading"><div><h1>{{ t('pts2UiHistoryMeasurementsTitle') }}</h1><p>{{ t('pts2UiHistoryMeasurementsHint') }}</p></div>
      <span v-if="updatedAt">{{ t('pts2UiHistoryUpdated', { time: date(updatedAt).split(', ').at(-1) }) }}</span></header>
    <div class="pts2-transactions-context">
      <Pts2ControllerSelector :controllers="context.controllers" :active-controller-id="context.activeControllerId"
        :disabled="loading || switching" @select="selectController" />
      <button class="pc-secondary" type="button" :disabled="refreshing || switching || denied || !context.activeControllerId" @click="refresh(); reloadOptions()">
        <ArrowPathIcon :class="{ spinning: refreshing }" aria-hidden="true" />{{ refreshing ? t('pts2UiHistoryRefreshing') : t('pts2UiHistoryRefresh') }}
      </button>
    </div>
    <p v-if="unapplied" class="notice" role="status">{{ t('pts2UiHistoryUnappliedChanges') }}</p>
    <p v-if="cachedStale" class="notice" role="status">{{ t('pts2UiHistoryRetainedStale') }}</p>
    <p v-if="error && page.items.length" class="notice error" role="alert">{{ error }}</p>
    <section v-if="!loading && !context.controllers.length" class="pts2-empty-state"><strong>{{ t('pts2UiNoStationControllers') }}</strong></section>
    <section v-else-if="!loading && context.selectionRequired && !context.activeControllerId" class="pts2-empty-state"><strong>{{ t('pts2UiChooseController') }}</strong><p>{{ t('pts2UiHistoryMeasurementsIsolation') }}</p></section>
    <section v-else class="pts2-transaction-workspace">
      <nav class="pts2-measurement-shortcuts" :aria-label="t('pts2UiHistoryMeasurementShortcuts')">
        <button v-for="shortcut in shortcuts" :key="shortcut.key" type="button" :class="[shortcut.key.toLowerCase(), { active: shortcut.active }]"
          :aria-pressed="shortcut.active" :disabled="loading || refreshing" @click="selectShortcut(shortcut.key)">
          <component :is="shortcut.icon" v-if="shortcut.icon" aria-hidden="true" /><span>{{ shortcut.label }}</span><strong>{{ shortcut.count }}</strong>
        </button>
        <Pts2HistoryExport journal="tank-measurements" :filters="criteria()" :scope="JSON.stringify([scope(), switching, denied])" :disabled="loading || refreshing || switching || denied || !context.activeControllerId">
          <button class="pts2-filter-toggle pc-secondary" type="button" :aria-expanded="filtersOpen" aria-controls="pts2-measurement-filter-panel" @click="filtersOpen = !filtersOpen">
            <FunnelIcon aria-hidden="true" />{{ t('pts2UiHistoryFilters') }} <span v-if="activeFilterGroups">{{ activeFilterGroups }}</span>
          </button>
        </Pts2HistoryExport>
      </nav>
      <section v-if="filtersOpen" id="pts2-measurement-filter-panel">
        <header><h2>{{ t('pts2UiHistoryFilterMeasurements') }}</h2><button type="button" :aria-label="t('pts2UiHistoryCloseFilters')" @click="filtersOpen = false">×</button></header>
        <Pts2TankMeasurementFilters :model-value="draft" @update:model-value="Object.assign(draft, $event)" :options="options" :loading="switching || denied"
          :range-error="filterError" @apply="applyFilters" @clear="clearFilters" @close="filtersOpen = false" @options="loadOption" />
      </section>
      <div v-if="activeChips.length" class="pts2-active-filter-chips" aria-live="polite">
        <Pts2FilterChip v-for="chip in activeChips" :key="chip.key" :entity-id="chip.entityId" :appearance="chip.appearance || 'neutral'" :label="chip.label" @remove="removeChip(chip.key)" />
        <button type="button" @click="clearFilters">{{ t('pts2UiHistoryClear') }}</button>
      </div>
      <section class="pts2-transaction-table-card pts2-measurement-table-card"><header><h2>{{ t('pts2UiHistoryMeasurements') }}</h2><span>{{ t('pts2UiHistoryShown', { count: page.items.length, total: page.totalItems }) }}</span></header>
        <p v-if="loading" class="pts2-drawer-state" role="status">{{ t('pts2UiHistoryLoadingMeasurements') }}</p>
        <div v-else-if="error && !page.items.length" class="pts2-empty-state" role="alert"><strong>{{ error }}</strong><button class="pc-secondary" type="button" @click="refresh()">{{ t('pts2UiHistoryRetry') }}</button></div>
        <div v-else-if="page.items.length" class="pts2-table-scroll"><table><thead><tr>
          <th>{{ t('pts2UiHistoryStatus') }}</th><th>{{ t('pts2UiHistoryMeasurementTime') }}</th><th>{{ t('pts2UiHistoryTankNumberLabel') }}</th><th>{{ t('pts2UiHistoryFuel') }}</th><th>{{ t('pts2UiHistoryAlarms') }}</th><th>{{ t('pts2UiHistoryVolumeLiters') }}</th><th>{{ t('pts2UiHistoryLevelMm') }}</th><th>{{ t('pts2UiHistoryWaterMm') }}</th><th>{{ t('pts2UiHistoryTemperatureShort') }}</th><th :aria-label="t('pts2UiHistoryDetails')"></th>
        </tr></thead><tbody><tr v-for="item in page.items" :key="item.id" :class="['measurement-' + item.displayState.toLowerCase(), { selected: selectedId === item.id }]" tabindex="0"
          @click="openMeasurement(item)" @keydown.enter.prevent="openMeasurement(item)" @keydown.space.prevent="openMeasurement(item)">
          <td :data-label="t('pts2UiHistoryStatus')"><span class="pts2-measurement-state" :class="item.displayState.toLowerCase()"><component :is="stateIcons[item.displayState]" aria-hidden="true" />{{ stateLabels[item.displayState] }}</span></td>
          <td :data-label="t('pts2UiHistoryMeasurementTime')">{{ date(item.eventAt || item.receivedAt, item.stationTimeZone) }}</td><td :data-label="t('pts2UiHistoryTankNumberLabel')">{{ item.tankNumber ?? '—' }}</td>
          <td :data-label="t('pts2UiHistoryFuel')"><Pts2EntityBadge :entity-id="item.fuelGradeId" :label="item.fuelGradeName || '—'" /></td><td :data-label="t('pts2UiHistoryAlarms')"><span class="pts2-alarm-badge" :class="{ active: item.alarms.length, unknown: item.alarmDataState !== 'KNOWN' }">{{ alarmText(item) }}</span></td>
          <td :data-label="t('pts2UiHistoryVolumeLiters')">{{ number(item.productVolume, 0) }}</td><td :data-label="t('pts2UiHistoryLevelMm')">{{ number(item.productHeight) }}</td><td :data-label="t('pts2UiHistoryWaterMm')">{{ number(item.waterHeight) }}</td><td :data-label="t('pts2UiHistoryTemperatureShort')">{{ number(item.temperature) }}</td>
          <td><button class="pts2-row-detail" type="button" :aria-label="t('pts2UiHistoryOpenMeasurement', { number: item.tankNumber ?? '—' })" @click.stop="openMeasurement(item)">›</button></td>
        </tr></tbody></table></div>
        <div v-else class="pts2-empty-state"><strong>{{ hasConditions ? t('pts2UiHistoryMeasurementsFilteredEmpty') : t('pts2UiHistoryMeasurementsEmpty') }}</strong><button v-if="hasConditions" class="pc-secondary" type="button" @click="clearFilters">{{ t('pts2UiHistoryClearFilters') }}</button></div>
        <footer v-if="page.totalItems" class="pts2-transaction-pagination"><label>{{ t('pts2UiHistoryRowsPerPage') }} <select :value="pageSize" :disabled="refreshing" @change="changePageSize($event.target.value)"><option :value="25">25</option><option :value="50">50</option><option :value="100">100</option></select></label>
          <span>{{ t('pts2UiHistoryPageRange', { from: resultFrom, to: resultTo, total: page.totalItems }) }}</span><nav :aria-label="t('pts2UiHistoryMeasurementPagination')"><button type="button" :aria-label="t('pts2UiHistoryPrevious')" :disabled="page.page <= 1 || refreshing" @click="goToPage(page.page - 1)">‹</button>
            <template v-for="button in pageButtons" :key="button"><span v-if="typeof button === 'string'">…</span><button v-else type="button" :class="{ active: button === page.page }" :aria-current="button === page.page ? 'page' : undefined" :disabled="refreshing" @click="goToPage(button)">{{ button }}</button></template>
            <button type="button" :aria-label="t('pts2UiHistoryNext')" :disabled="page.page >= page.totalPages || refreshing" @click="goToPage(page.page + 1)">›</button></nav>
        </footer>
      </section>
    </section>
    <Pts2TankMeasurementDrawer v-if="selectedId" :detail="detail" :loading="detailLoading" :error="detailError" @close="closeDrawer" />
  </section>
</template>
